import Carousel from 'bootstrap/js/src/carousel';

const MAX_SLIDE_HEIGHT = 500;

export default class AEBlockGallery {
	constructor( galleryElement, forceCaptionsAbove ) {
		this.element = galleryElement;
		this.forceCaptionsAbove = forceCaptionsAbove;

		// Bind methods to maintain correct 'this' context
		this.onResize = this.onResize.bind( this );
		this.refreshIndicators = this.refreshIndicators.bind( this );
		this.escapeHandler = this.escapeHandler.bind( this );
		this.resizeSlidesAndControls = this.resizeSlidesAndControls.bind( this );
		this.setCaptionsPosition = this.setCaptionsPosition.bind( this );

		this.init();

		return this;
	}

	init() {
		this.createCarousel();

		this.setActiveSlide();

		this.refreshIndicators();

		this.setCaptionsPosition();

		const images = Array.from(
			this.element.querySelectorAll( '.carousel-item img' )
		);
		const incompleteImages = images.filter( ( img ) => ! img.complete );

		this.resizeSlidesAndControls();

		incompleteImages.forEach( ( img ) => {
			img.onload = () => this.resizeSlidesAndControls();
		});

		this.attachFullScrListeners();

		// Use the pre-bound method reference
		window.addEventListener( 'resize', this.onResize, true );
	}

	onResize() {
		this.refreshIndicators();

		this.setCaptionsPosition();

		this.resizeSlidesAndControls();
	}

	createCarousel() {
		this.carousel = new Carousel( this.element );
	}

	to( idx ) {
		this.carousel.to( idx );
		const nodes = this.element.querySelectorAll( '.carousel-item' );
		const element = nodes[idx];
		if ( element && ! element.classList.contains( 'active' ) ) {
			element.classList.add( 'active' );
		}
	}

	dispose() {
		window.removeEventListener( 'resize', this.onResize );
		if ( this.carousel ) {
			this.carousel.dispose();
		}
	}

	setActiveSlide( idx = 0 ) {

		// count how many slides there are
		const slides = this.element.querySelectorAll( '.carousel-item' );

		if ( ! slides.length ) {
			return;
		}
		slides[idx].classList.add( 'active' );
	}

	goToNewSlide() {
		const nodes = this.element.querySelectorAll( '.carousel-item' );
		for ( let index = 0; index < nodes.length; index++ ) {
			const element = nodes[index];
			if ( element.querySelector( 'img[src*=no-img]' ) ) {
				this.to( index );
			}
		}
	}

	removeAllActive() {
		const nodes = this.element.querySelectorAll( '.carousel-item' );
		for ( let index = 0; index < nodes.length; index++ ) {
			const element = nodes[index];
			element.classList.remove( 'active' );
		}
	}

	refreshIndicators() {
		const dotCount = this.element.querySelectorAll( '.carousel-item' ).length;

		// Generate the HTML for the navigation dots
		let dotHTML = '';
		for ( let i = 0; i < dotCount; i++ ) {
			const activeClass = 0 === i ? 'active' : '';
			dotHTML += `<button type="button" data-bs-target="#${this.element.id}" data-bs-slide-to="${i}"  class="${activeClass}" aria-label="Slide ${i}"></button>`;
		}

		// Add the dots HTML to the DOM
		const indicators = this.element.querySelector( '.carousel-indicators' );
		if ( indicators ) {
			indicators.innerHTML = dotHTML;
		}
	}

	setCaptionsPosition() {
		if ( this.forceCaptionsAbove ) {
			return;
		}

		if ( 720 > window.innerWidth ) {
			let max = 0;

			const items = this.element.querySelectorAll( '.carousel-item' );
			for ( const item of items ) {
				item.classList.add( 'with-caption-below' );

				const caption = item.querySelector( '.carousel-caption' );
				if ( caption ) {
					caption.classList.add( 'carousel-caption-below' );

					caption.style.height = 'auto';

					const naturalHeight = caption.getBoundingClientRect().height;

					if ( naturalHeight > max ) {
						max = naturalHeight;
					}
				}
			}
			const captionsBelow = this.element.querySelectorAll(
				'.carousel-caption-below'
			);
			for ( const cap of captionsBelow ) {
				cap.style.heigth = max + 'px';
			}

			for ( const indicator of this.element.querySelectorAll(
				'.carousel-indicators'
			) ) {
				indicator.style.display = 'none';
			}
		} else {
			for ( const item of this.element.querySelectorAll( '.with-caption-below' ) ) {
				item.classList.remove( 'with-caption-below' );
				const cap = item.querySelector( '.carousel-caption' );
				if ( cap ) {
					cap.classList.remove( 'carousel-caption-below' );
				}
			}

			for ( const indicator of this.element.querySelectorAll(
				'.carousel-indicators'
			) ) {
				indicator.style.display = 'flex';
			}
		}
		this.resizeSlidesAndControls();
	}

	resizeSlidesAndControls() {
		let maxHeight = this.getMinMaxImgHeight();
		if ( 500 >= maxHeight ) {
			maxHeight = 500;
		}

		if ( 0 >= maxHeight ) {
			return;
		}

		for ( const item of this.element.querySelectorAll( '.carousel-item' ) ) {
			item.style.height = maxHeight + 'px';

			const img = item.querySelector( 'img' );
			if ( img ) {
				img.style.height = maxHeight + 'px';
			}
		}

		for ( const ctrl of this.element.querySelectorAll(
			'button[class*=carousel-control]'
		) ) {
			ctrl.style.height = maxHeight + 'px';
		}
	}

	getMinMaxImgHeight() {
		const heights = []; //create empty array to store height values
		let tallest; //create variable to make note of the tallest slide
		for ( const item of this.element.querySelectorAll( '.carousel-item img' ) ) {
			const h = item.naturalHeight;

			//add heights to array
			heights.push( h );

			tallest = Math.max.apply( null, heights ); //save largest value
		}

		if ( this.element.classList.contains( 'is-full-screen' ) ) {
			tallest = window.innerHeight;
		} else if ( MAX_SLIDE_HEIGHT < tallest ) {
			tallest = MAX_SLIDE_HEIGHT;
		}

		if ( 576 > window.innerWidth ) {

			// Force at least 500px images
			tallest = 500;
		}

		return tallest;
	}

	exitFullscreen() {
		this.element.classList.remove( 'is-full-screen' );

		if ( null !== document.fullscreenElement ) {
			if ( document.exitFullscreen ) {
				document.exitFullscreen();
			} else if ( document.webkitExitFullscreen ) {
				document.webkitExitFullscreen();
			} else if ( document.mozCancelFullScreen ) {
				document.mozCancelFullScreen();
			} else if ( document.msExitFullscreen ) {
				document.msExitFullscreen();
			}
		}
	}

	enterFullscreen() {
		this.element.classList.add( 'is-full-screen' );

		if ( this.element.requestFullscreen ) {
			this.element.requestFullscreen();
		} else if ( this.element.webkitRequestFullscreen ) {
			this.element.webkitRequestFullscreen();
		} else if ( this.element.mozRequestFullScreen ) {
			this.element.mozRequestFullScreen();
		} else if ( this.element.msRequestFullscreen ) {
			this.element.msRequestFullscreen();
		}

		this.resizeSlidesAndControls();
	}

	attachFullScrListeners() {
		document.addEventListener( 'fullscreenchange', this.escapeHandler );
		document.addEventListener( 'webkitfullscreenchange', this.escapeHandler );
		document.addEventListener( 'mozfullscreenchange', this.escapeHandler );
		document.addEventListener( 'msfullscreenchange', this.escapeHandler );

		if (
			document.fullscreenEnabled ||
			document.webkitFullscreenEnabled ||
			document.mozFullScreenEnabled ||
			document.msFullscreenEnabled
		) {

			// click event handler
			const triggerEl = this.element.querySelector(
				'.ae-block-gallery__fullscreen-trigger'
			);
			if ( ! triggerEl ) {
				return;
			}

			triggerEl.addEventListener(
				'mouseup',
				() => {
					const fullscreenElement = this.getFullscreenEl();

					if ( ! fullscreenElement ) {
						this.enterFullscreen();
					} else {
						this.exitFullscreen();
					}
				},
				false
			);
			triggerEl.addEventListener(
				'touchend',
				() => {
					const fullscreenElement = this.getFullscreenEl();

					if ( ! fullscreenElement ) {
						this.enterFullscreen();
					} else {
						this.exitFullscreen();
					}
				},
				false
			);
		}
	}

	getFullscreenEl() {
		return (
			document.fullscreenElement ||
			document.webkitFullscreenElement ||
			document.mozFullScreenElement ||
			document.msFullscreenElement
		);
	}

	escapeHandler() {

		/* escapeHandler */
		const fullscreenElement = this.getFullscreenEl();

		if ( ! fullscreenElement ) {
			this.exitFullscreen();
		}
	}
}
